/**
 * Combo renderer: bars (left axis) with a smooth line overlay (right axis). Static SVG only:
 * no scripts or <style>, so it renders the same in a README <img> as in the browser.
 */
import { GraphOptions } from '../options';
import { formatCompact } from '../shared/aggregate';
import { escapeXml } from '../shared/escape';
import { lineStroke, paint, round, svgDocument } from '../shared/svg';
import { PreparedData } from '../shared/types';
import { ComboLayout, layoutCombo } from './layout';

const DASH: Record<GraphOptions['gridStyle'], string> = {
    dotted: ' stroke-dasharray="1.5 4.5" stroke-linecap="round"',
    dashed: ' stroke-dasharray="6 5"',
    solid: '',
};

/** Bar with rounded top corners only. */
const barPath = (x: number, y: number, width: number, height: number, radius: number): string => {
    const r = Math.min(radius, width / 2, height);
    const bottom = y + height;
    if (r <= 0)
        return `M${round(x)} ${round(bottom)}V${round(y)}H${round(x + width)}V${round(bottom)}Z`;
    return (
        `M${round(x)} ${round(bottom)}V${round(y + r)}` +
        `A${round(r)} ${round(r)} 0 0 1 ${round(x + r)} ${round(y)}` +
        `H${round(x + width - r)}` +
        `A${round(r)} ${round(r)} 0 0 1 ${round(x + width)} ${round(y + r)}` +
        `V${round(bottom)}Z`
    );
};

const renderLegend = (layout: ComboLayout, options: GraphOptions): string => {
    if (!layout.legend) return '';
    const { fontSize, colors } = options;
    const y = layout.legend.y;
    const entries = layout.legend.entries.map((entry) => {
        const swatch =
            entry.kind === 'bar'
                ? `<rect x="${round(entry.x)}" y="${round(y - 6)}" width="12" height="12" rx="3" fill="${paint(colors.bar)}"/>`
                : `<line x1="${round(entry.x)}" y1="${round(y)}" x2="${round(entry.x + 12)}" y2="${round(y)}" stroke="${paint(colors.line)}" stroke-width="3" stroke-linecap="round"/>`;
        return (
            swatch +
            `<text x="${round(entry.x + 18)}" y="${round(y)}" dy="0.35em" font-size="${fontSize}" fill="${paint(colors.label)}">${escapeXml(entry.label)}</text>`
        );
    });
    return `<g>${entries.join('')}</g>`;
};

export const renderCombo = (data: PreparedData, options: GraphOptions): string => {
    const layout = layoutCombo(data, options);
    const { colors, fontSize } = options;
    const { plot } = layout;
    const parts: string[] = [];
    let defs = '';

    if (layout.title) {
        parts.push(
            `<text x="${round(layout.title.x)}" y="${layout.title.y}" text-anchor="middle" font-size="${layout.title.size}" font-weight="600" fill="${paint(colors.text)}">${escapeXml(layout.title.text)}</text>`,
        );
    }
    if (layout.total) {
        parts.push(
            `<text x="${round(layout.total.x)}" y="${layout.total.y}" text-anchor="middle" font-size="${fontSize}" fill="${paint(colors.count)}">${escapeXml(layout.total.text)}</text>`,
        );
    }

    // Grid (one set of lines, shared by both axes)
    const grid: string[] = [];
    if (options.showGridY) {
        for (const y of layout.gridY.slice(1))
            grid.push(
                `<line x1="${round(plot.left)}" y1="${round(y)}" x2="${round(plot.right)}" y2="${round(y)}"${DASH[options.gridStyle]}/>`,
            );
    }
    if (options.showGridX) {
        for (const label of layout.xLabels)
            grid.push(
                `<line x1="${round(label.x)}" y1="${round(plot.top)}" x2="${round(label.x)}" y2="${round(plot.bottom)}"${DASH[options.gridStyle]}/>`,
            );
    }
    grid.push(
        `<line x1="${round(plot.left)}" y1="${round(plot.bottom)}" x2="${round(plot.right)}" y2="${round(plot.bottom)}"/>`,
    );
    parts.push(
        `<g stroke="${paint(colors.grid)}" stroke-opacity="${round(options.gridOpacity / 100)}" stroke-width="1">${grid.join('')}</g>`,
    );

    // Axis labels
    const labelAttrs = `font-size="${fontSize}" fill="${paint(colors.label)}"`;
    if (layout.leftTicks.length) {
        const x = round(plot.left - fontSize * 0.7);
        parts.push(
            `<g ${labelAttrs} text-anchor="end">` +
                layout.leftTicks
                    .map((t) => `<text x="${x}" y="${round(t.y)}" dy="0.35em">${t.label}</text>`)
                    .join('') +
                '</g>',
        );
    }
    if (layout.rightTicks.length) {
        const x = round(plot.right + fontSize * 0.7);
        parts.push(
            `<g ${labelAttrs} text-anchor="start">` +
                layout.rightTicks
                    .map((t) => `<text x="${x}" y="${round(t.y)}" dy="0.35em">${t.label}</text>`)
                    .join('') +
                '</g>',
        );
    }
    if (options.showXLabels) {
        const y = round(plot.bottom + fontSize * 1.6);
        parts.push(
            `<g ${labelAttrs} text-anchor="middle">` +
                layout.xLabels
                    .map((l) => `<text x="${round(l.x)}" y="${y}">${escapeXml(l.text)}</text>`)
                    .join('') +
                '</g>',
        );
    }

    const describe = (index: number) => {
        const item = layout.items[index];
        const values = [
            ...(layout.showBars ? [`${layout.barLabel}: ${formatCompact(item.barValue)}`] : []),
            ...(layout.showLine ? [`${layout.lineLabel}: ${formatCompact(item.lineValue)}`] : []),
        ];
        return `<title>${escapeXml(`${item.title} · ${values.join(', ')}`)}</title>`;
    };

    // Bars
    if (layout.showBars) {
        const bars = layout.items
            .filter((item) => item.bar.height > 0)
            .map((item) => {
                const d = barPath(
                    item.bar.x,
                    item.bar.y,
                    item.bar.width,
                    item.bar.height,
                    options.barRadius,
                );
                const index = layout.items.indexOf(item);
                return options.showTooltips
                    ? `<path d="${d}">${describe(index)}</path>`
                    : `<path d="${d}"/>`;
            });
        const opacity = round(options.barOpacity / 100);
        parts.push(
            `<g fill="${paint(colors.bar)}"${opacity < 1 ? ` fill-opacity="${opacity}"` : ''}>${bars.join('')}</g>`,
        );
    }

    // Line + points
    if (layout.showLine && layout.linePath) {
        const stroke = lineStroke(options, plot.left, plot.right);
        defs += stroke.defs;
        parts.push(
            `<path d="${layout.linePath}" fill="none" ${stroke.attrs} stroke-width="${options.lineWidth}" stroke-linejoin="round" stroke-linecap="round"/>`,
        );
        if (layout.pointRadius > 0) {
            const border = Math.min(options.pointBorderWidth, layout.pointRadius);
            const circles = layout.items.map((item, i) => {
                const attrs = `cx="${round(item.x)}" cy="${round(item.lineY)}" r="${round(layout.pointRadius)}"`;
                return options.showTooltips && !layout.showBars
                    ? `<circle ${attrs}>${describe(i)}</circle>`
                    : `<circle ${attrs}/>`;
            });
            parts.push(
                `<g fill="${paint(colors.point)}"${border ? ` stroke="${paint(colors.pointBorder)}" stroke-width="${round(border)}"` : ''}>${circles.join('')}</g>`,
            );
        }
    }

    parts.push(renderLegend(layout, options));

    return svgDocument({
        width: layout.width,
        height: layout.height,
        options,
        title: layout.title?.text ?? `${data.username}'s Contribution Activity`,
        description: data.totalText,
        defs,
        body: parts.join(''),
    });
};
